import type { IHostAnalyticsManager } from '../../../services/analytics/IHostAnalyticsManager';
import type { InstanceLaunchedEvent } from '../../../services/playjector/interfaces/IPlayjectorService';
import type { GameInstanceLifecycle, GameInstanceStore } from '../GameInstanceStore';
import { NOT_PROVISIONED_BLOCK_REASON } from './AndroidInstanceLaunchManager';

/**
 * Default restart budget: at most 3 policy restarts per instance per rolling
 * hour; attempts fire 5s → 30s → 2m after the failure. Sized so a transient
 * crash recovers quickly while a permanently broken VM stops thrashing
 * (android-multi-instance-host Phase 4). All configurable via constructor opts.
 */
export const DEFAULT_RECOVERY_MAX_RESTARTS = 3;
export const DEFAULT_RECOVERY_WINDOW_MS = 3_600_000;
export const DEFAULT_RECOVERY_BACKOFF_MS: readonly number[] = [5_000, 30_000, 120_000];

/**
 * How long a restart (policy or intentional) waits for the relaunched
 * instance's `instanceLaunched` echo before the attempt counts as failed.
 *
 * This must cover a full Android cold reboot, because the C++ restart is now
 * asynchronous: `ProcessDirectory::restartInstance` acks immediately and emits
 * the `instanceLaunched` echo only after the emulator has rebooted. The
 * AndroidLauncher reboot budget is kBootTimeout(90s) + kAdbReadyTimeout(45s) +
 * OpenPackage(>=30s) + window/F11 placement (~12s) ~= 180s worst case; 200s
 * leaves headroom so a genuinely-recovered instance's echo is never discarded
 * (which would strand it in `failed` and burn crash budget). Keep this in sync
 * with those AndroidLauncher.cpp timeouts.
 */
export const DEFAULT_RELAUNCH_ECHO_TIMEOUT_MS = 200_000;

/** Reported availability-block override left on a record whose budget ran out. */
export const RECOVERY_EXHAUSTED_REASON = 'recovery_exhausted';

export type InstanceRecoveryOptions = {
  maxRestartsPerWindow?: number;
  windowMs?: number;
  backoffMs?: readonly number[];
  relaunchEchoTimeoutMs?: number;
};

export type InstanceRecoveryDeps = {
  store: GameInstanceStore;
  /** Frozen Phase 3 surface: restart one instance; siblings untouched. */
  restartInstance: (streamId: string, gameInstanceIndex: number) => Promise<string>;
  /** Active android stream id (GameInstanceLaunchManager); undefined after teardown. */
  getActiveStreamId: () => string | undefined;
  /** Lifecycle analytics sink; optional so the policy works headless in tests. */
  analytics?: Pick<IHostAnalyticsManager, 'sendGameInstanceLifecycleEvent'>;
};

type RecoveryEntry = {
  /** Restart-attempt timestamps inside the rolling window. */
  attempts: number[];
  /** Pending scheduled restart. */
  timer?: ReturnType<typeof setTimeout>;
  /** Pending relaunch-echo wait after a restart was issued. */
  echoTimer?: ReturnType<typeof setTimeout>;
  /**
   * Which path is driving the currently queued/in-flight restart. Drives the
   * `restart_issued` analytics reason at dispatch (crash -> the failure reason;
   * intentional -> restart-on-disconnect).
   */
  kind?: 'crash' | 'intentional';
};

/** States from which an intentional restart may be marked (store-legal sources). */
const INTENTIONAL_RESTART_SOURCES: readonly GameInstanceLifecycle[] = ['ready', 'occupied', 'cooldown', 'failed'];

/**
 * InstanceRecoveryManager (android-multi-instance-host Phase 4) — the restart
 * POLICY for crashed instances.
 *
 * - `onInstanceFailed` (a crash surfaced via `instanceClosed`) schedules a
 *   budgeted `restartInstance`: at most `maxRestartsPerWindow` attempts per
 *   rolling `windowMs`, spaced by `backoffMs`. Each attempt marks the record
 *   `restarting` first so a concurrent/late `instanceClosed` is recognized as
 *   expected and does not re-trigger the policy.
 * - Records whose `availabilityBlockReason` is `not_provisioned` (missing game
 *   content — android-game-warming Phase 2) are never scheduled: no budget is
 *   consumed and no restart is sent. They thaw only via a later successful
 *   launch for the index.
 * - A successful relaunch (`instanceLaunched` echo → record back to `ready`)
 *   resets the budget. A failed reply/echo returns the record to `failed` and
 *   the next backoff step is scheduled.
 * - Budget exhaustion leaves the record `failed` with
 *   `availabilityBlockReason: 'recovery_exhausted'` (kept in the v2 report) and
 *   stops sending restarts.
 * - Teardown / campaign switch / manual close cancel pending restarts: the
 *   store subscription cancels any entry whose record is gone or `stopping`;
 *   `cancelAll` clears everything.
 *
 * Also owns the instance-lifecycle analytics events (launch failed, restart
 * issued, recovery exhausted) — each carries `gameInstanceIndex`.
 */
export class InstanceRecoveryManager {
  private readonly entries = new Map<number, RecoveryEntry>();
  private readonly launchFailuresNotified = new Set<string>();
  private unsubscribeStore: (() => void) | null = null;

  // ---- Global restart serialization (one outstanding restart at a time) ----
  //
  // Restarts from BOTH the crash path (`runAttempt`) and the intentional path
  // (`requestIntentionalRestart`) funnel through a single FIFO. Rebooting an
  // emulator tears down its virtual display; several rebooting at once triggers
  // simultaneous DXGI keyed-mutex abandonment on the capture side (green
  // siblings). Serializing to one in-flight restart avoids that, and the
  // `pendingRestarts` dedup guard stops a duplicate trigger from stacking a
  // second REAL restart for the same instance.
  /** Indices queued or in-flight; blocks a duplicate restart from stacking. */
  private readonly pendingRestarts = new Set<number>();
  /** FIFO of indices awaiting dispatch (deduped via `pendingRestarts`). */
  private readonly restartQueue: number[] = [];
  /** The single index whose `restartInstance` is currently outstanding, or null. */
  private inFlightIndex: number | null = null;

  private readonly maxRestartsPerWindow: number;
  private readonly windowMs: number;
  private readonly backoffMs: readonly number[];
  private readonly relaunchEchoTimeoutMs: number;

  constructor(
    private readonly deps: InstanceRecoveryDeps,
    options: InstanceRecoveryOptions = {},
  ) {
    this.maxRestartsPerWindow = options.maxRestartsPerWindow ?? DEFAULT_RECOVERY_MAX_RESTARTS;
    this.windowMs = options.windowMs ?? DEFAULT_RECOVERY_WINDOW_MS;
    this.backoffMs = options.backoffMs ?? DEFAULT_RECOVERY_BACKOFF_MS;
    this.relaunchEchoTimeoutMs = options.relaunchEchoTimeoutMs ?? DEFAULT_RELAUNCH_ECHO_TIMEOUT_MS;
  }

  /** Subscribe to the store (ready resets, teardown cancels, launch-failure analytics). */
  public attach(): void {
    if (this.unsubscribeStore) return;
    this.unsubscribeStore = this.deps.store.addListener(() => this.reconcile());
  }

  public detach(): void {
    this.unsubscribeStore?.();
    this.unsubscribeStore = null;
    this.cancelAll('recovery manager detached');
  }

  // ========================================
  // Policy entry points
  // ========================================

  /** An instance transitioned to `failed` (crash): schedule a budgeted restart. */
  public onInstanceFailed(gameInstanceIndex: number): void {
    const session = this.deps.store.getSessionByIndex(gameInstanceIndex);
    if (!session || session.status !== 'failed') return;
    // android-game-warming Phase 2: a `not_provisioned` record is a
    // provisioning state, not a crash — never schedule recovery for it (no
    // budget consumed, no restart sent, no exhaustion override).
    if (session.availabilityBlockReason === NOT_PROVISIONED_BLOCK_REASON) return;
    const entry = this.entryFor(gameInstanceIndex);
    this.clearTimer(entry);
    this.scheduleRestart(gameInstanceIndex, entry);
  }

  /** The instance reached `ready`: recovery succeeded — reset its budget. */
  public onInstanceReady(gameInstanceIndex: number): void {
    const entry = this.entries.get(gameInstanceIndex);
    const wasManaged =
      entry !== undefined ||
      this.pendingRestarts.has(gameInstanceIndex) ||
      this.inFlightIndex === gameInstanceIndex;
    if (entry) {
      this.clearTimer(entry);
      this.clearEchoTimer(entry);
      this.entries.delete(gameInstanceIndex);
    }
    if (!wasManaged) return;
    // Settle the global slot so the next queued restart can dispatch.
    this.releaseInFlight(gameInstanceIndex);
    this.dispatchNext();
  }

  /**
   * Mark an intentional (dev-controls / manual) restart BEFORE the restart
   * call is issued: the record enters `restarting`, so a concurrent
   * `instanceClosed` is treated as expected and the policy does not fight the
   * restart. The relaunch echo (or its timeout) settles the outcome — a
   * failed intentional restart becomes `failed` and the policy takes over.
   */
  public noteIntentionalRestart(gameInstanceIndex: number): void {
    const session = this.deps.store.getSessionByIndex(gameInstanceIndex);
    if (!session || !INTENTIONAL_RESTART_SOURCES.includes(session.status)) return;
    this.deps.store.transition(session.sessionId, 'restarting');
    this.armEchoWait(gameInstanceIndex, this.entryFor(gameInstanceIndex));
  }

  /**
   * Restart an instance intentionally and issue the restart in one step —
   * the restart-on-player-leave path (`restartOnDisconnect`). Mirrors the
   * dev-controls / admin restart: mark the record `restarting` first (so a
   * concurrent `instanceClosed` is treated as expected), then issue
   * `restartInstance`; the relaunch echo (or its timeout) settles the outcome.
   *
   * Unlike the crash path this does NOT consume the recovery budget — a clean
   * player-leave restart is not a failure. A no-op when there is no active
   * stream or the record is not in a restartable state.
   */
  public requestIntentionalRestart(gameInstanceIndex: number): void {
    const streamId = this.deps.getActiveStreamId();
    if (!streamId) return;
    // In-flight guard: a restart for this index is already queued or
    // outstanding. Re-issuing here (a repeated player-leave, or a manual
    // restart racing an auto one) is exactly what stormed the instance with
    // duplicate REAL restarts — no-op instead.
    if (this.pendingRestarts.has(gameInstanceIndex)) return;
    const session = this.deps.store.getSessionByIndex(gameInstanceIndex);
    if (!session || !INTENTIONAL_RESTART_SOURCES.includes(session.status)) return;
    this.deps.store.transition(session.sessionId, 'restarting');
    const entry = this.entryFor(gameInstanceIndex);
    entry.kind = 'intentional';
    this.enqueueRestart(gameInstanceIndex);
  }

  /**
   * Relaunch echo for a restarting instance: success → `ready` (the store
   * subscription resets the budget); failure → the attempt failed. Returns
   * true when the echo matched a restarting instance; false otherwise (e.g.
   * launch-path replies, which the launch manager owns).
   *
   * Phase 8 (review fix S1): echoes from a stale stream (a previous campaign's
   * still-registered streamId) are refused before the index-only session
   * lookup can corrupt the new campaign's instance.
   */
  public onInstanceLaunched(event: InstanceLaunchedEvent): boolean {
    const activeStreamId = this.deps.getActiveStreamId();
    if (activeStreamId !== undefined && event.streamId !== activeStreamId) return false;
    const session = this.deps.store.getSessionByIndex(event.gameInstanceIndex);
    if (!session || session.status !== 'restarting') return false;
    const entry = this.entries.get(event.gameInstanceIndex);
    if (event.result === 'success') {
      if (entry) this.clearEchoTimer(entry);
      this.deps.store.transition(session.sessionId, 'ready');
      return true;
    }
    this.attemptFailed(
      event.gameInstanceIndex,
      event.error ?? `Instance relaunch failed: ${event.result}`,
    );
    return true;
  }

  /** Teardown / campaign switch / detach: cancel every pending restart. */
  public cancelAll(reason: string): void {
    for (const entry of this.entries.values()) {
      this.clearTimer(entry);
      this.clearEchoTimer(entry);
    }
    this.entries.clear();
    this.pendingRestarts.clear();
    this.restartQueue.length = 0;
    this.inFlightIndex = null;
    if (reason) console.log(`[InstanceRecoveryManager] Cancelled all pending restarts (${reason})`);
  }

  // ========================================
  // Internal — scheduling and attempt lifecycle
  // ========================================

  private scheduleRestart(gameInstanceIndex: number, entry: RecoveryEntry): void {
    entry.attempts = entry.attempts.filter((at) => Date.now() - at < this.windowMs);
    if (entry.attempts.length >= this.maxRestartsPerWindow) {
      this.exhaust(gameInstanceIndex);
      return;
    }
    const delay = this.backoffMs[Math.min(entry.attempts.length, this.backoffMs.length - 1)];
    entry.timer = setTimeout(() => this.runAttempt(gameInstanceIndex), delay);
  }

  private runAttempt(gameInstanceIndex: number): void {
    const entry = this.entryFor(gameInstanceIndex);
    entry.timer = undefined;
    const session = this.deps.store.getSessionByIndex(gameInstanceIndex);
    const streamId = this.deps.getActiveStreamId();
    // Torn down, recovered by someone else, or the whole stream is gone.
    if (!session || session.status !== 'failed' || !streamId) {
      this.clearEchoTimer(entry);
      this.releaseInFlight(gameInstanceIndex);
      this.entries.delete(gameInstanceIndex);
      this.dispatchNext();
      return;
    }
    entry.attempts.push(Date.now());
    entry.kind = 'crash';
    this.deps.store.transition(session.sessionId, 'restarting');
    // Serialize the actual send through the global FIFO: the analytics event,
    // echo-wait, and `restartInstance` call all happen in `dispatchNext` when
    // this index reaches the front and no other restart is outstanding.
    this.enqueueRestart(gameInstanceIndex);
  }

  // ========================================
  // Internal — global restart serialization
  // ========================================

  /** Queue an index for a serialized restart (deduped); pump the queue. */
  private enqueueRestart(gameInstanceIndex: number): void {
    if (this.pendingRestarts.has(gameInstanceIndex)) return;
    this.pendingRestarts.add(gameInstanceIndex);
    this.restartQueue.push(gameInstanceIndex);
    this.dispatchNext();
  }

  /** Release an index's hold on the queue/in-flight slot. */
  private releaseInFlight(gameInstanceIndex: number): void {
    this.pendingRestarts.delete(gameInstanceIndex);
    const queued = this.restartQueue.indexOf(gameInstanceIndex);
    if (queued >= 0) this.restartQueue.splice(queued, 1);
    if (this.inFlightIndex === gameInstanceIndex) this.inFlightIndex = null;
  }

  /**
   * Dispatch the next queued restart when nothing is outstanding. Sends the
   * `restart_issued` analytics, arms the relaunch-echo wait, and issues the
   * single `restartInstance`. A success reply keeps the echo wait armed (the
   * `instanceLaunched` echo or its timeout settles); any other reply/rejection
   * fails the attempt. An "already in flight" rejection from our own messenger
   * dedup is a no-op — never a failure.
   */
  private dispatchNext(): void {
    if (this.inFlightIndex !== null) return;
    const gameInstanceIndex = this.restartQueue.shift();
    if (gameInstanceIndex === undefined) return;

    const streamId = this.deps.getActiveStreamId();
    const session = this.deps.store.getSessionByIndex(gameInstanceIndex);
    if (!streamId || !session || session.status !== 'restarting') {
      // No longer dispatchable (torn down / recovered / stream gone). Drop it
      // and try the next queued index.
      this.pendingRestarts.delete(gameInstanceIndex);
      this.dispatchNext();
      return;
    }

    this.inFlightIndex = gameInstanceIndex;
    const entry = this.entryFor(gameInstanceIndex);
    this.armEchoWait(gameInstanceIndex, entry);
    this.deps.analytics?.sendGameInstanceLifecycleEvent({
      lifecycleEvent: 'restart_issued',
      gameInstanceIndex,
      campaignId: session.campaignId,
      reason: entry.kind === 'intentional' ? 'restart_on_disconnect' : session.failure,
    });
    this.deps.restartInstance(streamId, gameInstanceIndex)
      .then((result) => {
        // Cancelled/settled while the reply was in flight (teardown, cancelAll,
        // echo already landed) — do nothing.
        if (this.inFlightIndex !== gameInstanceIndex) return;
        if (result !== 'success') {
          this.attemptFailed(gameInstanceIndex, `Instance restart replied: ${result}`);
        }
        // success: keep the echo wait armed; the launch echo (or its timeout) settles.
      })
      .catch((error: unknown) => {
        if (this.inFlightIndex !== gameInstanceIndex) return;
        const message = error instanceof Error ? error.message : String(error);
        // Defensive: our own serialization never double-sends, but if the
        // messenger's per-key dedup ever rejects, that is not a launch failure.
        if (message.includes('already in flight')) return;
        this.attemptFailed(gameInstanceIndex, message);
      });
  }

  /** Wait for the relaunched instance's echo; a missing echo fails the attempt. */
  private armEchoWait(gameInstanceIndex: number, entry: RecoveryEntry): void {
    this.clearEchoTimer(entry);
    entry.echoTimer = setTimeout(() => {
      this.attemptFailed(
        gameInstanceIndex,
        `Timed out after ${this.relaunchEchoTimeoutMs}ms waiting for the relaunched instance`,
      );
    }, this.relaunchEchoTimeoutMs);
  }

  private attemptFailed(gameInstanceIndex: number, reason: string): void {
    const entry = this.entries.get(gameInstanceIndex);
    if (!entry) return;
    this.clearTimer(entry);
    this.clearEchoTimer(entry);
    const session = this.deps.store.getSessionByIndex(gameInstanceIndex);
    if (!session || session.status !== 'restarting') {
      this.releaseInFlight(gameInstanceIndex);
      this.entries.delete(gameInstanceIndex);
      this.dispatchNext();
      return;
    }
    console.warn(`[InstanceRecoveryManager] Restart of instance ${gameInstanceIndex} failed: ${reason}`);
    this.deps.store.transition(session.sessionId, 'failed', reason);
    // Free the global slot BEFORE scheduling the next backoff so a sibling can
    // restart while this one waits out its backoff. The scheduled `runAttempt`
    // re-enqueues this index when its timer fires.
    this.releaseInFlight(gameInstanceIndex);
    this.scheduleRestart(gameInstanceIndex, entry);
    this.dispatchNext();
  }

  private exhaust(gameInstanceIndex: number): void {
    const session = this.deps.store.getSessionByIndex(gameInstanceIndex);
    if (session && session.status === 'failed') {
      this.deps.store.setAvailabilityBlockReason(session.sessionId, RECOVERY_EXHAUSTED_REASON);
      this.deps.analytics?.sendGameInstanceLifecycleEvent({
        lifecycleEvent: 'recovery_exhausted',
        gameInstanceIndex,
        campaignId: session.campaignId,
        reason: session.failure,
      });
      console.warn(
        `[InstanceRecoveryManager] Instance ${gameInstanceIndex} restart budget exhausted ` +
        `(${this.maxRestartsPerWindow} per ${this.windowMs}ms) — leaving failed`,
      );
    }
    this.entries.delete(gameInstanceIndex);
  }

  // ========================================
  // Internal — store reconciliation
  // ========================================

  private reconcile(): void {
    const sessions = this.deps.store.getSessions();
    const byIndex = new Map(sessions.map((s) => [s.gameInstanceIndex, s]));

    let released = false;
    for (const [index, entry] of [...this.entries]) {
      const session = byIndex.get(index);
      if (!session || session.status === 'stopping') {
        // Teardown / campaign switch / manual close cancels pending restarts.
        this.clearTimer(entry);
        this.clearEchoTimer(entry);
        this.entries.delete(index);
        this.releaseInFlight(index);
        released = true;
      } else if (session.status === 'ready') {
        this.onInstanceReady(index);
      }
    }
    // A torn-down in-flight/queued restart freed the global slot — let the next
    // queued restart dispatch.
    if (released) this.dispatchNext();

    // Launch-failure analytics (Phase 4): a pre-ready failure lands on the
    // record as `launchError`; emit once per failed session record.
    const failedSessionIds = new Set<string>();
    for (const session of sessions) {
      if (session.status !== 'failed' || !session.launchError) continue;
      failedSessionIds.add(session.sessionId);
      if (this.launchFailuresNotified.has(session.sessionId)) continue;
      this.launchFailuresNotified.add(session.sessionId);
      this.deps.analytics?.sendGameInstanceLifecycleEvent({
        lifecycleEvent: 'launch_failed',
        gameInstanceIndex: session.gameInstanceIndex,
        campaignId: session.campaignId,
        reason: session.launchError,
      });
    }
    for (const sessionId of [...this.launchFailuresNotified]) {
      if (!failedSessionIds.has(sessionId)) this.launchFailuresNotified.delete(sessionId);
    }
  }

  private entryFor(gameInstanceIndex: number): RecoveryEntry {
    let entry = this.entries.get(gameInstanceIndex);
    if (!entry) {
      entry = { attempts: [] };
      this.entries.set(gameInstanceIndex, entry);
    }
    return entry;
  }

  private clearTimer(entry: RecoveryEntry): void {
    if (entry.timer) clearTimeout(entry.timer);
    entry.timer = undefined;
  }

  private clearEchoTimer(entry: RecoveryEntry): void {
    if (entry.echoTimer) clearTimeout(entry.echoTimer);
    entry.echoTimer = undefined;
  }
}
